// =======================
// sear/src/paginas/paginaEscola/perfil/atosMant.jsx
// =======================

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Typography,
} from "@mui/material";

import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import { jsPDF } from "jspdf";
import { api } from "@api/api";
import { formatarPrazoValidade } from "../../../componentes/comuns/prazoValidade";

export default function ListaAtos() {
  const [atos, setAtos] = useState([]);
  const [atoSelecionado, setAtoSelecionado] = useState(null);

  const [loading, setLoading] = useState(true);

  const formatarDataSemFuso = (dataString) => {
    if (!dataString) return "";

    const [ano, mes, dia] = String(dataString).slice(0, 10).split("-");

    return `${dia}/${mes}/${ano}`;
  };

  useEffect(() => {
    const fetchAtos = async () => {
      try {
        setLoading(true); // Certifique-se de setar loading como true antes da requisição

        const token = localStorage.getItem("token");

        const response = await fetch(api.maintainerActs(), {
          method: "GET",
          credentials: "include",
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json",
          },
        });

        const data = await response.json();

        if (Array.isArray(data)) {
          setAtos(data);
          setAtoSelecionado(null);
        } else {
          console.error("Os dados retornados não são um array:", data);
        }
      } catch (error) {
        console.error("Erro ao buscar atos:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchAtos();
  }, []); // O array vazio [] garante que a requisição será feita apenas uma vez ao montar o componente

  const ordenarAtosPorData = (listaAtos) => {
    if (!Array.isArray(listaAtos)) return [];

    return [...listaAtos].sort((a, b) => new Date(b.data) - new Date(a.data));
  };

  const montarTituloAto = (ato) => {
    if (!ato) return "";

    const partes = [
      ato.tipo,
      ato.orgao_emissor,
      ato.numero_parecer_ou_portaria
        ? `Nº ${ato.numero_parecer_ou_portaria}`
        : "",
      ato.retificacao ? "— Retificação" : "",
    ];

    return partes.filter(Boolean).join(" ");
  };

  const criarDataSemFuso = (dataString) => {
    if (!dataString) return null;

    const [ano, mes, dia] = String(dataString)
      .slice(0, 10)
      .split("-")
      .map(Number);

    return new Date(ano, mes - 1, dia);
  };

  const obterSituacaoVigencia = (ato) => {
    const inicio = criarDataSemFuso(ato?.inicio_da_validade);
    const fim = criarDataSemFuso(ato?.final_da_validade);

    if (!inicio || !fim) return null;

    const hoje = new Date();

    hoje.setHours(0, 0, 0, 0);

    // Vigência ainda não iniciada: não apresenta classificação.
    if (hoje < inicio) {
      return null;
    }

    if (hoje > fim) {
      return {
        nome: "Vencido",
        cor: "#d14343",
        fundo: "#fff1f1",
        progresso: 100,
      };
    }

    const tempoTotal = fim.getTime() - inicio.getTime();
    const tempoDecorrido = hoje.getTime() - inicio.getTime();

    const progresso =
      tempoTotal > 0
        ? Math.min(100, Math.max(0, (tempoDecorrido / tempoTotal) * 100))
        : 0;

    if (progresso >= 50) {
      return {
        nome: "Próximo de vencer",
        cor: "#d97706",
        fundo: "#fff7ed",
        progresso,
      };
    }

    return {
      nome: "Vigente",
      cor: "#16a34a",
      fundo: "#ecfdf3",
      progresso,
    };
  };

  const ordenarAtosPorPrioridade = (listaAtos) => {
    if (!Array.isArray(listaAtos)) return [];

    const prioridade = {
      Vencido: 0,
      "Próximo de vencer": 1,
      Vigente: 2,
    };

    return [...listaAtos].sort((atoA, atoB) => {
      const situacaoA = obterSituacaoVigencia(atoA)?.nome;
      const situacaoB = obterSituacaoVigencia(atoB)?.nome;

      const prioridadeA = prioridade[situacaoA] ?? 3;
      const prioridadeB = prioridade[situacaoB] ?? 3;

      if (prioridadeA !== prioridadeB) {
        return prioridadeA - prioridadeB;
      }

      return new Date(atoB.data) - new Date(atoA.data);
    });
  };

  const gerarPDF = () => {
    const doc = new jsPDF();
    const atosOrdenados = ordenarAtosPorData(atos);
    const margin = 10; // Margem de 10mm
    const pageWidth = doc.internal.pageSize.getWidth();
    const contentWidth = pageWidth - 2 * margin;
    const maxPageHeight = doc.internal.pageSize.getHeight() - margin;

    // Configuração para quebra de palavras longas
    doc.setProperties({
      splitLongWords: true,
      lineBreakFactor: 0.9,
    });

    let yPosition = margin; // Variável para controlar a posição Y manualmente

    atosOrdenados.forEach((ato) => {
      // Guarda a página inicial do ato
      const startPage = doc.internal.getCurrentPageInfo().pageNumber;
      const startY = yPosition;

      // Array para armazenar as informações de retângulo por página
      const rectInfo = [];

      rectInfo.push({
        page: startPage,
        startY: startY,
      });

      // Cabeçalho
      doc.setFontSize(8);
      doc.setFont(undefined, "bold");

      const headerText = `IOF MG - Diário do Executivo - ${formatarDataSemFuso(
        ato.data,
      )} p.${ato.pagina} Col. ${ato.coluna}`;

      const headerLines = doc.splitTextToSize(headerText, contentWidth);

      // Verifica espaço para cabeçalho
      if (yPosition + headerLines.length * 5 > maxPageHeight) {
        doc.addPage();
        yPosition = margin;

        rectInfo.push({
          page: doc.internal.getCurrentPageInfo().pageNumber,
          startY: yPosition,
        });
      }

      doc.text(headerLines, margin + 2, yPosition + 5);
      yPosition += headerLines.length * 5 + 3;

      // Título
      doc.setFontSize(12);

      const titulo = `${ato.tipo} ${ato.orgao_emissor} Nº - ${
        ato.numero_parecer_ou_portaria
      }${ato.retificacao ? " - Retificação" : ""}`;

      const tituloLines = doc.splitTextToSize(titulo, contentWidth);

      // Verifica espaço para título
      if (yPosition + tituloLines.length * 6 > maxPageHeight) {
        doc.addPage();
        yPosition = margin;

        rectInfo.push({
          page: doc.internal.getCurrentPageInfo().pageNumber,
          startY: yPosition,
        });
      }

      doc.text(
        tituloLines,
        margin +
          (contentWidth -
            (doc.getStringUnitWidth(titulo) * 12) / doc.internal.scaleFactor) /
            2,
        yPosition + 5,
      );

      yPosition += tituloLines.length * 6 + 5;

      // Número SEI (se existir)
      if (ato.incluir_n_sei) {
        doc.setFontSize(10);

        const seiText = `Nº SEI: ${ato.incluir_n_sei}`;
        const seiLines = doc.splitTextToSize(seiText, contentWidth);

        // Verifica espaço para número SEI
        if (yPosition + seiLines.length * 5 > maxPageHeight) {
          doc.addPage();
          yPosition = margin;

          rectInfo.push({
            page: doc.internal.getCurrentPageInfo().pageNumber,
            startY: yPosition,
          });
        }

        doc.text(
          seiLines,
          margin +
            (contentWidth -
              (doc.getStringUnitWidth(seiText) * 10) /
                doc.internal.scaleFactor) /
              2,
          yPosition + 5,
        );

        yPosition += seiLines.length * 5 + 5;
      }

      // Corpo do texto
      doc.setFontSize(10);
      doc.setFont(undefined, "normal");

      const lines = doc.splitTextToSize(ato.corpo_do_texto, contentWidth - 4);

      for (let i = 0; i < lines.length; i++) {
        // Verifica se precisa de nova página
        if (yPosition + 5 > maxPageHeight) {
          doc.addPage();
          yPosition = margin;

          rectInfo.push({
            page: doc.internal.getCurrentPageInfo().pageNumber,
            startY: yPosition,
          });
        }

        doc.text(lines[i], margin + 2, yPosition + 5);
        yPosition += 5; // Espaçamento entre linhas
      }

      yPosition += 5; // Espaço após o texto

      // Validade (se existir)
      if (ato.inicio_da_validade && ato.final_da_validade) {
        doc.setFontSize(8);

        const validadeText = `Validade: ${formatarDataSemFuso(
          ato.inicio_da_validade,
        )} - ${formatarDataSemFuso(
          ato.final_da_validade,
        )} - Prazo de validade: ${formatarPrazoValidade(
          ato.inicio_da_validade,
          ato.final_da_validade,
        )}`;

        const validadeLines = doc.splitTextToSize(validadeText, contentWidth);

        // Verifica espaço para validade
        if (yPosition + validadeLines.length * 4 > maxPageHeight) {
          doc.addPage();
          yPosition = margin;

          rectInfo.push({
            page: doc.internal.getCurrentPageInfo().pageNumber,
            startY: yPosition,
          });
        }

        doc.text(validadeLines, margin + 2, yPosition + 5);
        yPosition += validadeLines.length * 4 + 2; // Adiciona espaço extra após a validade
      }

      // Desenha os retângulos em todas as páginas do ato (AGORA DEPOIS DA VALIDADE)
      const uniquePages = [...new Set(rectInfo.map((info) => info.page))];
      const endY = yPosition;

      uniquePages.forEach((pageNum, i) => {
        doc.setPage(pageNum);

        // Encontra a posição Y inicial nesta página
        const pageInfo = rectInfo.find((info) => info.page === pageNum);
        const rectYStart = pageInfo ? pageInfo.startY : margin;

        // Define a posição Y final
        let rectYEnd;

        if (i === uniquePages.length - 1) {
          // Última página - usa a posição Y final
          rectYEnd = endY;
        } else {
          // Páginas intermediárias - vai até o final da página
          rectYEnd = maxPageHeight;
        }

        // Desenha o retângulo
        doc.setDrawColor(0);
        doc.setLineWidth(0.5);

        // Lados esquerdo e direito
        doc.line(margin, rectYStart, margin, rectYEnd);

        doc.line(
          margin + contentWidth,
          rectYStart,
          margin + contentWidth,
          rectYEnd,
        );

        // Parte superior (apenas na primeira página)
        if (i === 0) {
          doc.line(margin, rectYStart, margin + contentWidth, rectYStart);
        }

        // Parte inferior (apenas na última página)
        if (i === uniquePages.length - 1) {
          doc.line(margin, rectYEnd, margin + contentWidth, rectYEnd);
        }
      });

      // Espaço entre atos
      if (yPosition + 15 > maxPageHeight) {
        doc.addPage();
        yPosition = margin;
      } else {
        yPosition += 15;
      }
    });

    doc.save("atos.pdf");
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: 280,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={32} />
      </Box>
    );
  }

  const atosOrdenados = ordenarAtosPorPrioridade(atos);
  const atoAtual = atoSelecionado || atosOrdenados[0];

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: {
            xs: "flex-start",
            md: "center",
          },
          justifyContent: "space-between",
          flexDirection: {
            xs: "column",
            md: "row",
          },
          gap: 2,
          marginBottom: 3,
        }}
      >
        <Box>
          <Typography
            variant="h4"
            sx={{
              color: "#0767d8",
              fontWeight: 700,
            }}
          >
            Atos da Mantenedora
          </Typography>

          <Typography
            variant="body1"
            sx={{
              color: "#5f6f94",
              marginTop: 0.5,
            }}
          >
            Consulte os atos publicados e acompanhe a vigência.
          </Typography>
        </Box>
      </Box>

      {atosOrdenados.length === 0 ? (
        <Paper
          variant="outlined"
          sx={{
            padding: 4,
            borderColor: "#c9def4",
            textAlign: "center",
            boxShadow: "none",
          }}
        >
          <Typography sx={{ color: "#5f6f94" }}>
            Nenhum ato encontrado.
          </Typography>
        </Paper>
      ) : (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              md: "300px minmax(0, 1fr)",
            },
            border: "1px solid #c9def4",
            borderRadius: 2,
            overflow: "hidden",
            backgroundColor: "#ffffff",
          }}
        >
          <Box
            sx={{
              padding: 2,
              borderRight: {
                xs: 0,
                md: "1px solid #dce8f5",
              },
              borderBottom: {
                xs: "1px solid #dce8f5",
                md: 0,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
                marginBottom: 2,
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  color: "#0767d8",
                  fontWeight: 700,
                }}
              >
                Atos publicados
              </Typography>

              <Chip
                label={atosOrdenados.length}
                size="small"
                sx={{
                  color: "#0767d8",
                  backgroundColor: "#eaf4ff",
                  fontWeight: 700,
                }}
              />
            </Box>

            <Box
              sx={{
                display: "grid",
                gap: 1.5,
                maxHeight: 540,
                overflowY: "auto",
                paddingRight: 0.5,
              }}
            >
              {atosOrdenados.map((ato) => {
                const selecionado = atoAtual?.id === ato.id;
                const situacao = obterSituacaoVigencia(ato);

                return (
                  <Paper
                    key={ato.id}
                    component="button"
                    type="button"
                    onClick={() => setAtoSelecionado(ato)}
                    variant="outlined"
                    sx={{
                      width: "100%",
                      padding: 2,
                      border: "1px solid",
                      borderColor: selecionado ? "#9fcdff" : "#dce8f5",
                      borderLeft: selecionado
                        ? "4px solid #087cf0"
                        : "4px solid transparent",
                      borderRadius: 2,
                      backgroundColor: selecionado ? "#f8fbff" : "#ffffff",
                      boxShadow: "none",
                      textAlign: "left",
                      cursor: "pointer",
                      fontFamily: "inherit",

                      "&:hover": {
                        borderColor: "#9fcdff",
                        backgroundColor: "#f8fbff",
                      },
                    }}
                  >
                    <Typography
                      variant="caption"
                      sx={{
                        color: "#5f6f94",
                      }}
                    >
                      {formatarDataSemFuso(ato.data)}
                      {ato.orgao_emissor ? ` · ${ato.orgao_emissor}` : ""}
                    </Typography>

                    <Typography
                      variant="body1"
                      sx={{
                        color: "#0767d8",
                        fontWeight: 700,
                        marginTop: 0.5,
                      }}
                    >
                      {montarTituloAto(ato)}
                    </Typography>

                    {situacao &&
                      ato.inicio_da_validade &&
                      ato.final_da_validade && (
                        <Box sx={{ marginTop: 1.75 }}>
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                              gap: 1,
                            }}
                          >
                            <Box
                              sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 0.75,
                              }}
                            >
                              <CalendarMonthOutlinedIcon
                                sx={{
                                  color: "#087cf0",
                                  fontSize: 19,
                                }}
                              />

                              <Typography
                                variant="caption"
                                sx={{
                                  color: "#4f5f85",
                                  fontWeight: 600,
                                }}
                              >
                                Vigência
                              </Typography>
                            </Box>

                            <Chip
                              label={situacao.nome}
                              size="small"
                              sx={{
                                height: 24,
                                color: situacao.cor,
                                backgroundColor: situacao.fundo,
                              }}
                            />
                          </Box>

                          <Box
                            sx={{
                              position: "relative",
                              height: 4,
                              marginTop: 1.5,
                              borderRadius: 10,
                              backgroundColor: "#dce5ef",
                            }}
                          >
                            <Box
                              sx={{
                                width: `${situacao.progresso}%`,
                                height: "100%",
                                borderRadius: 10,
                                backgroundColor: situacao.cor,
                              }}
                            />

                            {["Vigente", "Próximo de vencer"].includes(
                              situacao.nome,
                            ) && (
                              <Box
                                sx={{
                                  position: "absolute",
                                  left: `${situacao.progresso}%`,
                                  top: "50%",
                                  width: 11,
                                  height: 11,
                                  border: "2px solid #ffffff",
                                  borderRadius: "50%",
                                  backgroundColor: situacao.cor,
                                  boxShadow: `0 0 0 1px ${situacao.cor}`,
                                  transform: "translate(-50%, -50%)",
                                }}
                              />
                            )}
                          </Box>

                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              marginTop: 0.75,
                            }}
                          >
                            <Typography
                              variant="caption"
                              sx={{
                                color: "#5f6f94",
                              }}
                            >
                              {formatarDataSemFuso(ato.inicio_da_validade)}
                            </Typography>

                            <Typography
                              variant="caption"
                              sx={{
                                color: "#5f6f94",
                              }}
                            >
                              {formatarDataSemFuso(ato.final_da_validade)}
                            </Typography>
                          </Box>
                        </Box>
                      )}
                  </Paper>
                );
              })}
            </Box>
          </Box>

          <Box
            sx={{
              padding: {
                xs: 2,
                md: 2.5,
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: {
                  xs: "flex-start",
                  md: "center",
                },
                justifyContent: "space-between",
                flexDirection: {
                  xs: "column",
                  md: "row",
                },
                gap: 2,
                marginBottom: 2,
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 42,
                    height: 42,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    borderRadius: 2,
                    color: "#087cf0",
                    backgroundColor: "#eaf4ff",
                  }}
                >
                  <DescriptionOutlinedIcon />
                </Box>

                <Typography
                  variant="h6"
                  sx={{
                    color: "#0767d8",
                    fontWeight: 700,
                  }}
                >
                  {montarTituloAto(atoAtual)}
                </Typography>
              </Box>

              <Button
                variant="outlined"
                startIcon={<DownloadOutlinedIcon />}
                onClick={gerarPDF}
                sx={{
                  minHeight: 42,
                  paddingX: 2,
                  color: "#0767d8",
                  borderColor: "#087cf0",
                  backgroundColor: "#ffffff",
                  textTransform: "none",
                  boxShadow: "none",

                  "&:hover": {
                    color: "#ffffff",
                    borderColor: "#087cf0",
                    backgroundColor: "#087cf0",
                    boxShadow: "none",
                  },
                }}
              >
                Baixar PDF
              </Button>
            </Box>

            <Paper
              variant="outlined"
              sx={{
                minHeight: 420,
                padding: {
                  xs: 2,
                  md: 3,
                },
                borderColor: "#dce8f5",
                borderRadius: 2,
                boxShadow: "none",
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#5f6f94",
                }}
              >
                IOF MG - Diário do Executivo -{" "}
                {formatarDataSemFuso(atoAtual.data)}
                {atoAtual.pagina ? ` p.${atoAtual.pagina}` : ""}
                {atoAtual.coluna ? ` Col. ${atoAtual.coluna}` : ""}
              </Typography>

              <Typography
                variant="h6"
                align="center"
                sx={{
                  color: "#243b64",
                  fontWeight: 700,
                  marginTop: 2,
                }}
              >
                {montarTituloAto(atoAtual)}
              </Typography>

              {atoAtual.incluir_n_sei && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4f5f85",
                    fontWeight: 600,
                    marginTop: 2,
                  }}
                >
                  Nº SEI: {atoAtual.incluir_n_sei}
                </Typography>
              )}

              <Typography
                variant="body2"
                align="justify"
                sx={{
                  color: "#33446b",
                  lineHeight: 1.65,
                  marginTop: 2,
                  whiteSpace: "pre-wrap",
                  overflowWrap: "anywhere",
                }}
              >
                {atoAtual.corpo_do_texto}
              </Typography>

              {atoAtual.inicio_da_validade && atoAtual.final_da_validade && (
                <Typography
                  variant="body2"
                  sx={{
                    color: "#4f5f85",
                    marginTop: 2.5,
                  }}
                >
                  Validade: {formatarDataSemFuso(atoAtual.inicio_da_validade)} a{" "}
                  {formatarDataSemFuso(atoAtual.final_da_validade)} — Prazo de
                  validade:{" "}
                  {formatarPrazoValidade(
                    atoAtual.inicio_da_validade,
                    atoAtual.final_da_validade,
                  )}
                </Typography>
              )}
            </Paper>
          </Box>
        </Box>
      )}
    </Box>
  );
}
